import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { onboardingErrorMessage } from "../api/onboarding";

const endpoint = "/api/secure/founder-memory";
const inputClass = "mt-1 w-full rounded-lg border border-slate-300 bg-white p-3 text-slate-900";
const buttonClass = "rounded-lg bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50";
const blankForm = () => ({ kind: "customer", contact: "", company: "", event: "",
  conversation_date: new Date().toLocaleDateString("en-CA"), notes: "" });

export default function FounderMemory() {
  const [form, setForm] = useState(blankForm);
  const submissionId = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState(null);

  async function load() {
    const { data } = await api.get(`${endpoint}/conversations`);
    setConversations(data.conversations);
    setTotal(data.total);
  }
  useEffect(() => {
    load().catch(err => setError(onboardingErrorMessage(err, "Could not load your conversations.")))
      .finally(() => setLoading(false));
  }, []);

  async function save(event) {
    event.preventDefault();
    setSaving(true); setError(""); setNotice("");
    // Retain the ID after a failed request so retries cannot duplicate a conversation.
    submissionId.current = submissionId.current || crypto.randomUUID();
    try {
      const { data } = await api.post(`${endpoint}/conversations`, { ...form, id: submissionId.current });
      submissionId.current = null;
      setForm(blankForm()); setReport(null); setExpanded(data.id);
      setNotice("Conversation saved. Find patterns again to include this note.");
      await load();
    } catch (err) {
      setError(onboardingErrorMessage(err, "Could not save your conversation. Your draft is still here."));
    } finally { setSaving(false); }
  }

  async function analyze() {
    setAnalyzing(true); setError(""); setReport(null);
    try {
      const { data } = await api.post(`${endpoint}/patterns`);
      setReport(data);
    } catch (err) {
      setError(onboardingErrorMessage(err, "Could not analyze patterns. Your conversations are saved."));
    } finally { setAnalyzing(false); }
  }

  function evidenceLink(evidence, index) {
    const source = conversations.find(item => item.id === evidence.conversation_id);
    return <li key={`${evidence.conversation_id}-${index}`} className="mt-2 text-sm">
      <blockquote className="border-l-2 border-slate-300 pl-3 whitespace-pre-wrap">“{evidence.excerpt}”</blockquote>
      <a href={`#conversation-${evidence.conversation_id}`} onClick={() => setExpanded(evidence.conversation_id)}
        className="text-primary underline">{source ? `${source.contact} · ${source.company || source.kind} · ${source.conversation_date}` : "View conversation"}</a>
    </li>;
  }

  return <main className="mx-auto max-w-4xl space-y-6 px-6 py-6 text-slate-900 dark:text-slate-100">
    <div><h1 className="text-2xl font-bold">Founder Memory</h1>
      <p className="mt-1 text-slate-500">Preserve what happened in customer and investor conversations. See what repeats.</p>
      <p className="mt-2 text-sm text-slate-500">Private to your account. Notes are saved without emailing anyone.</p></div>
    {error && <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-800">{error}</div>}
    {notice && <div role="status" className="rounded-lg bg-green-50 p-4 text-green-800">{notice}</div>}

    <form onSubmit={save} className="rounded-xl border border-slate-200 p-5">
      <h2 className="text-lg font-semibold">Capture a conversation</h2>
      <fieldset disabled={saving || analyzing || loading} className="mt-4 grid gap-4 sm:grid-cols-2">
        <label>Conversation type<select className={inputClass} value={form.kind}
          onChange={e => setForm({ ...form, kind: e.target.value })}>
          <option value="customer">Customer</option><option value="investor">Investor</option>
        </select></label>
        <label>Date<input required type="date" className={inputClass} value={form.conversation_date}
          onChange={e => setForm({ ...form, conversation_date: e.target.value })} /></label>
        <label>Who did you meet?<input required maxLength={200} className={inputClass} value={form.contact}
          onChange={e => setForm({ ...form, contact: e.target.value })} /></label>
        <label>Company / firm<input maxLength={200} className={inputClass} value={form.company}
          onChange={e => setForm({ ...form, company: e.target.value })} /></label>
        <label className="sm:col-span-2">Event / context (optional)<input maxLength={200} className={inputClass} value={form.event}
          onChange={e => setForm({ ...form, event: e.target.value })} /></label>
        <label className="sm:col-span-2">What do you want to remember?
          <p className="mt-1 text-sm text-slate-500">What resonated? What concerns or ideas came up? What did they explicitly request or commit to? Who follows up, and when? Keep your interpretation separate from what they said.</p>
          <textarea required rows={7} maxLength={6000} className={inputClass} value={form.notes}
            placeholder="Type, paste, or use your phone keyboard’s dictation while the conversation is fresh."
            onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
        <div><button className={buttonClass} disabled={!form.notes.trim() || !form.contact.trim()}>
          {saving ? "Saving…" : "Save conversation"}</button></div>
      </fieldset>
    </form>

    <section className="rounded-xl border border-slate-200 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-semibold">Patterns across conversations</h2>
        <button className={buttonClass} onClick={analyze} disabled={loading || saving || analyzing || conversations.length < 3}>
          {analyzing ? "Finding patterns…" : "Find patterns"}</button></div>
      <p className="mt-2 text-sm text-slate-500">{loading ? "Loading conversations…" : conversations.length < 3
        ? `Save ${3 - conversations.length} more conversation${3 - conversations.length === 1 ? "" : "s"} to look for recurring signals.`
        : `Compare your latest ${conversations.length} saved conversations${total > conversations.length ? ` (${total} saved in total)` : ""}. Early signals, with sources—not conclusions about the market.`}</p>
      {report && <div className="mt-4 space-y-4">
        <p className="text-sm text-slate-500">Based on {report.conversation_count} conversations. AI interpretations: check the source notes.</p>
        {report.unverified_pattern_count > 0 && <p role="status" className="text-amber-700">Some proposed patterns could not be verified against the original notes. Try finding patterns again; this does not mean there are no recurring themes.</p>}
        {report.patterns.length === 0 && !report.unverified_pattern_count && <p>No recurring pattern supported by at least two conversations yet.</p>}
        {report.patterns.map((pattern, index) => <article key={index} className="rounded-lg bg-slate-50 p-4 text-slate-900">
          <p className="text-xs uppercase text-slate-500">{pattern.category} · {new Set(pattern.evidence.map(item => item.conversation_id)).size} conversations</p>
          <h3 className="mt-1 font-semibold">{pattern.theme}</h3><p className="mt-1">{pattern.interpretation}</p>
          <ul>{pattern.evidence.map(evidenceLink)}</ul>
        </article>)}
        {report.follow_ups.length > 0 && <div><h3 className="font-semibold">Explicit follow-ups</h3>
          {report.follow_ups.map((item, index) => <div key={index} className="mt-3"><p>{item.action}</p>
            {item.status === "waiting_on_milestone" && <p className="text-sm text-slate-500">Waiting on milestone</p>}
            {item.status === "unclear" && <p className="text-sm text-slate-500">Status unclear — check the source notes</p>}<ul>{evidenceLink(item.evidence, index)}</ul></div>)}
        </div>}
      </div>}
    </section>

    <section><h2 className="text-lg font-semibold">Saved conversations ({total})</h2>
      {conversations.map(item => <article key={item.id} id={`conversation-${item.id}`} className="mt-3 scroll-mt-4 rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold">{item.contact}{item.company ? ` · ${item.company}` : ""}</h3>
        <p className="text-sm text-slate-500">{item.kind} · {item.conversation_date}{item.event ? ` · ${item.event}` : ""}</p>
        <details open={expanded === item.id} className="mt-3"><summary className="cursor-pointer">Original notes</summary>
          <p className="mt-2 whitespace-pre-wrap">{item.notes}</p></details>
      </article>)}
    </section>
  </main>;
}
